import React, { FormEvent, useCallback, useEffect } from 'react';
import { Idol } from 'types';
import Modal from 'components/UI/Modal';
import ManualCreationForm from './ManualCreationForm';
import useIdolForm from './hooks/useIdolForm';
import useUniqueIdols from './hooks/useUniqueIdols';
import { UNIQUE_IDOLS } from './constants/uniqueIdols';
import { ITEM_TYPES } from 'constants/index';

interface EditIdolFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveIdol: (idol: Idol) => void;
  idol: Idol | null;
}

const EditIdolForm: React.FC<EditIdolFormProps> = ({ isOpen, onClose, onSaveIdol, idol }) => {
  // Use the idol form hook
  const {
    newIdolName,
    setNewIdolName,
    newIdolSize,
    setNewIdolSize,
    prefixes,
    setPrefixes,
    suffixes,
    setSuffixes,
    availablePrefixes,
    availableSuffixes,
    prefixContentTypes,
    setSelectedPrefixContentType,
    suffixContentTypes,
    setSelectedSuffixContentType,
    createRegularIdol,
    resetForm
  } = useIdolForm(onSaveIdol, onClose);

  // Use the unique idols hook
  const {
    selectedUniqueIdol,
    setSelectedUniqueIdol,
    idolModifiers,
    setIdolModifiers,
    selectedVariableModifiers,
    setSelectedVariableModifiers,
    updateModifierValue,
    replaceVariableModifier,
    createUniqueIdol,
    isUniqueFormValid,
    resetUniqueIdolForm,
    isVariableModifierSelected
  } = useUniqueIdols();

  // Load idol data when editing an existing idol
  useEffect(() => {
    if (isOpen && idol) {
      // Set basic properties
      setNewIdolName(idol.name || '');
      
      // Determine idol size
      const sizeKey = Object.entries(ITEM_TYPES).find(([_, value]) => 
        value.name === idol.size?.name
      )?.[0] || 'MINOR';
      setNewIdolSize(sizeKey);

      // Handle unique idols
      if (sizeKey === 'UNIQUE') {
        // Try to find matching unique idol from constants
        const matchingIdol = UNIQUE_IDOLS.find(uniqueIdol => 
          idol.name?.includes(uniqueIdol.name)
        );
        
        if (matchingIdol) {
          setSelectedUniqueIdol(matchingIdol.id);
          
          // Special handling for Twisted Wish unique idol
          if (matchingIdol.id === 'twisted-wish') {
            // Extract variable modifiers from the idol stats
            const variableModIds = idol.stats
              .filter(stat => {
                // Find variable modifiers by checking against the idol's variableModifiers array
                const isVariable = matchingIdol.variableModifiers?.some(vm => vm.modId === stat.modId);
                return isVariable;
              })
              .map(stat => stat.modId);
            
            // Set selected variable modifiers
            setSelectedVariableModifiers(variableModIds);
            
            // Set values for ALL modifiers (including variables)
            idol.stats.forEach(stat => {
              // Always update the modifier value regardless of type
              updateModifierValue(stat.modId, stat.value);
            });
          } else {
            // Process variable modifiers for other unique idols if any
            if (matchingIdol.variableModifiers && matchingIdol.variableModifiersRequired) {
              // Map idol stats to variable modifiers
              const variableModIds = idol.stats
                .filter(stat => {
                  // Find if this is a variable modifier for this unique idol
                  return matchingIdol.variableModifiers?.some(vm => vm.modId === stat.modId);
                })
                .map(stat => stat.modId);
                
              setSelectedVariableModifiers(variableModIds);
              
              // Set values for all modifiers
              idol.stats.forEach(stat => {
                // Find if this stat exists in any modifier type
                const isFixedValue = matchingIdol.fixedValueModifiers?.some(fvm => fvm.modId === stat.modId);
                const isVariable = matchingIdol.variableModifiers?.some(vm => vm.modId === stat.modId);
                
                if (isFixedValue || isVariable) {
                  updateModifierValue(stat.modId, stat.value);
                }
              });
            }
          }
        }
      } else {
        // Handle regular idol prefixes and suffixes
        const prefixStats = idol.stats.filter(stat => stat.explicitType === 'prefix');
        const suffixStats = idol.stats.filter(stat => stat.explicitType === 'suffix');
        
        // Convert stats to form modifiers
        const newPrefixes = prefixStats.map(stat => ({
          text: availablePrefixes.find(mod => mod.id === stat.modId)?.description || '',
          value: stat.value.toString()
        }));
        
        const newSuffixes = suffixStats.map(stat => ({
          text: availableSuffixes.find(mod => mod.id === stat.modId)?.description || '',
          value: stat.value.toString()
        }));
        
        // Ensure at least one empty slot
        if (newPrefixes.length === 0) newPrefixes.push({ text: '', value: '' });
        if (newSuffixes.length === 0) newSuffixes.push({ text: '', value: '' });
        
        setPrefixes(newPrefixes);
        setSuffixes(newSuffixes);
      }
    }
  }, [idol, isOpen, availablePrefixes, availableSuffixes, setNewIdolName, setNewIdolSize, 
      setPrefixes, setSuffixes, setSelectedUniqueIdol, setSelectedVariableModifiers, updateModifierValue]);

  // This effect ensures form is reset when modal is closed
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      resetUniqueIdolForm();
    }
  }, [isOpen, resetForm, resetUniqueIdolForm]);
  
  // Handle form submission - preserve original id when editing
  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    
    try {
      // Handle unique idols differently
      if (newIdolSize === 'UNIQUE') {
        // If we have a name but no selection, try to find the matching idol first
        if (!selectedUniqueIdol && newIdolName) {
          const matchingIdol = UNIQUE_IDOLS.find(i => i.name === newIdolName);
          if (matchingIdol) {
            // Create idol with the found ID and preserve original ID
            const newIdol = createUniqueIdol(matchingIdol.name);
            const editedIdol = {
              ...newIdol,
              id: idol?.id || newIdol.id // Preserve original ID if editing
            };
            onSaveIdol(editedIdol);
            onClose();
            return;
          }
        }
  
        // Normal validation path
        if (!selectedUniqueIdol) {
          throw new Error("Please select a unique idol.");
        }
  
        if (!isUniqueFormValid()) {
          const selectedIdolData = UNIQUE_IDOLS.find(i => i.id === selectedUniqueIdol);
          if (selectedIdolData?.isSpecial && selectedIdolData?.variableModifiersRequired) {
            throw new Error(`Please select exactly ${selectedIdolData.variableModifiersRequired} variable modifiers.`);
          } else {
            throw new Error("Please complete the unique idol configuration.");
          }
        }
  
        // Special handling for Twisted Wish idol
        if (selectedUniqueIdol === 'twisted-wish' && idol) {
          // Create new idol (this will contain all new modifier changes)
          const newIdol = createUniqueIdol(newIdolName);
          
          // Create the edited idol with preserved ID
          const editedIdol = {
            ...newIdol,
            id: idol.id
          };
          
          onSaveIdol(editedIdol);
        } else {
          // For other unique idols
          const newIdol = createUniqueIdol(newIdolName);
          const editedIdol = {
            ...newIdol,
            id: idol?.id || newIdol.id // Preserve original ID if editing
          };
          onSaveIdol(editedIdol);
        }
      } else {
        // For regular idols
        const newIdol = createRegularIdol();
        const editedIdol = {
          ...newIdol,
          id: idol?.id || newIdol.id // Preserve original ID if editing
        };
        onSaveIdol(editedIdol);
      }
      
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
      alert(error instanceof Error ? error.message : String(error));
    }
  }, [
    newIdolSize, 
    selectedUniqueIdol, 
    newIdolName,
    createUniqueIdol, 
    isUniqueFormValid, 
    createRegularIdol, 
    onSaveIdol,
    onClose,
    idol
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Idol">
      <ManualCreationForm 
        newIdolName={newIdolName}
        setNewIdolName={setNewIdolName}
        newIdolSize={newIdolSize}
        setNewIdolSize={() => {/* Disabled to prevent size change when editing */}}
        prefixes={prefixes}
        setPrefixes={setPrefixes}
        suffixes={suffixes}
        setSuffixes={setSuffixes}
        availablePrefixes={availablePrefixes}
        availableSuffixes={availableSuffixes}
        prefixContentTypes={prefixContentTypes}
        setSelectedPrefixContentType={setSelectedPrefixContentType}
        suffixContentTypes={suffixContentTypes}
        setSelectedSuffixContentType={setSelectedSuffixContentType}
        handleSubmit={handleSubmit}
        onClose={onClose}
        // Unique idol props
        selectedUniqueIdol={selectedUniqueIdol}
        setSelectedUniqueIdol={setSelectedUniqueIdol}
        idolModifiers={idolModifiers}
        setIdolModifiers={setIdolModifiers}
        selectedVariableModifiers={selectedVariableModifiers}
        setSelectedVariableModifiers={setSelectedVariableModifiers}
        updateModifierValue={updateModifierValue}
        replaceVariableModifier={replaceVariableModifier}
        isVariableModifierSelected={isVariableModifierSelected}
        isUniqueFormValid={isUniqueFormValid}
        isEditing={true}
      />
    </Modal>
  );
};

export default EditIdolForm;